import React, { Component } from 'react';
import { Table, Spin, Card, Row, Col, Statistic, Tag, Button, Upload, message, Modal, Input, Popconfirm, Progress, Steps, Alert } from 'antd';
import { UploadOutlined, DeleteOutlined, FileTextOutlined, DatabaseOutlined, EyeOutlined } from '@ant-design/icons';
import { fetchData } from '../../../../lib/apis';
import { AccountMetaData } from '../../../../config/settings';
import moment from 'moment';

const { Step } = Steps;

export default class AIDocumentsManager extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            uploading: false,
            uploadStatus: 'idle',
            uploadPercent: 0,
            processingPercent: 0,
            uploadStage: 'idle',
            uploadMessage: '',
            uploadFileName: '',
            uploadEvents: [],
            files: [],
            total: 0,
            stats: null,
            page: 1,
            pageSize: 20,
            category: '',
            tags: '',
            previewVisible: false,
            previewContent: null,
            previewLoading: false
        };
    }

    componentDidMount() {
        this.loadFiles();
        this.loadStats();
    }

    loadFiles = async () => {
        try {
            this.setState({ loading: true });
            const { page, pageSize } = this.state;
            const companyId = this.props.companySelected?.company_id || 0;

            const res = await fetchData({
                url: `api/v1/app/ai/documents`,
                data: {
                    company_id: companyId,
                    limit: pageSize,
                    offset: (page - 1) * pageSize
                }
            });

            this.setState({
                files: res.data || [],
                total: res.total || 0,
                loading: false
            });
        } catch (err) {
            this.setState({ loading: false });
            message.error('Không thể tải danh sách tài liệu');
        }
    };

    loadStats = async () => {
        try {
            const companyId = this.props.companySelected?.company_id || 0;
            const res = await fetchData({
                url: `api/v1/app/ai/documents/stats`,
                data: { company_id: companyId }
            });
            this.setState({ stats: res.data });
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    getApiBaseUrl = () => {
        return (window._env_ && window._env_.REACT_APP_API_CHOTSALE_URL)
            || process.env.REACT_APP_API_CHOTSALE_URL
            || window.location.origin;
    };

    updateProcessingProgress = (event) => {
        this.setState((previousState) => {
            const previousEvents = previousState.uploadEvents || [];
            const lastEvent = previousEvents[previousEvents.length - 1];
            const nextEvents = lastEvent?.stage === event.stage && lastEvent?.message === event.message
                ? previousEvents
                : [...previousEvents, { ...event, id: `${Date.now()}-${Math.random()}` }].slice(-8);

            return {
                uploadStatus: event.stage === 'completed' ? 'success' : 'processing',
                uploadStage: event.stage || previousState.uploadStage,
                uploadMessage: event.message || previousState.uploadMessage,
                processingPercent: Math.max(
                    previousState.processingPercent || 0,
                    Number(event.percent) || 0
                ),
                uploadEvents: nextEvents
            };
        });
    };

    uploadDocumentWithProgress = (formData, file) => new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const endpoint = `${this.getApiBaseUrl().replace(/\/$/, '')}/api/v1/app/ai/documents/upload/stream`;
        let responseOffset = 0;
        let streamBuffer = '';
        let completed = false;
        let result;
        let streamError;

        const parseFrame = (frame) => {
            const lines = frame.split('\n');
            let eventName = 'message';
            const dataLines = [];
            lines.forEach((line) => {
                if (line.startsWith('event:')) eventName = line.slice(6).trim();
                if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
            });
            if (dataLines.length === 0) return;

            let data;
            try {
                data = JSON.parse(dataLines.join('\n'));
            } catch (error) {
                return;
            }

            if (eventName === 'progress') {
                this.updateProcessingProgress(data);
                return;
            }
            if (eventName === 'complete') {
                completed = true;
                result = data?.data;
                this.updateProcessingProgress({
                    stage: 'completed',
                    message: 'Tài liệu đã sẵn sàng để AI sử dụng',
                    percent: 100
                });
                return;
            }
            if (eventName === 'error') {
                streamError = new Error(data?.message || 'Không thể xử lý tài liệu');
                streamError.code = data?.code;
            }
        };

        const consumeResponse = () => {
            const nextText = xhr.responseText.slice(responseOffset).replace(/\r/g, '');
            responseOffset = xhr.responseText.length;
            streamBuffer += nextText;
            let separatorIndex = streamBuffer.indexOf('\n\n');
            while (separatorIndex >= 0) {
                parseFrame(streamBuffer.slice(0, separatorIndex));
                streamBuffer = streamBuffer.slice(separatorIndex + 2);
                separatorIndex = streamBuffer.indexOf('\n\n');
            }
        };

        xhr.open('POST', endpoint, true);
        xhr.withCredentials = true;
        xhr.timeout = 0;
        xhr.setRequestHeader('Authorization', AccountMetaData.getToken());
        xhr.setRequestHeader('Company', AccountMetaData.getCompanyId());

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
            this.setState({
                uploadStatus: 'uploading',
                uploadPercent: percent,
                uploadStage: 'uploading',
                uploadMessage: `Đang tải ${file.name} lên máy chủ (${percent}%)`
            });
        };
        xhr.upload.onload = () => {
            this.setState({
                uploadPercent: 100,
                uploadStatus: 'processing',
                uploadStage: 'received',
                uploadMessage: 'Đã tải xong file, đang chờ hệ thống AI tiếp nhận'
            });
        };
        xhr.onprogress = consumeResponse;
        xhr.onload = () => {
            consumeResponse();
            if (streamBuffer.trim()) parseFrame(streamBuffer);
            if (xhr.status < 200 || xhr.status >= 300) {
                reject(new Error(`Máy chủ từ chối tài liệu (HTTP ${xhr.status})`));
                return;
            }
            if (streamError) {
                reject(streamError);
                return;
            }
            if (!completed) {
                reject(new Error('Kết nối tiến độ bị đóng trước khi xử lý hoàn tất'));
                return;
            }
            resolve(result);
        };
        xhr.onerror = () => reject(new Error('Mất kết nối khi tải tài liệu'));
        xhr.onabort = () => reject(new Error('Đã hủy tải tài liệu'));
        xhr.send(formData);
    });

    handleUpload = async (info) => {
        const { file } = info;
        const { category, tags } = this.state;
        const companyId = this.props.companySelected?.company_id || 0;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('companyId', companyId);
        if (category) formData.append('category', category);
        if (tags) formData.append('tags', tags);
        formData.append('uploadedBy', this.props.auth_login?.name || 'Admin');

        this.setState({
            uploading: true,
            uploadStatus: 'uploading',
            uploadPercent: 0,
            processingPercent: 0,
            uploadStage: 'uploading',
            uploadMessage: `Đang chuẩn bị tải ${file.name}`,
            uploadFileName: file.name,
            uploadEvents: []
        });

        try {
            const res = await this.uploadDocumentWithProgress(formData, file);

            if (res.code === 200) {
                message.success(`File ${file.name} đã được tải lên thành công! Tạo ra ${res.data?.chunks || 0} chunks.`);
                info.onSuccess?.(res, file);
                this.loadFiles();
                this.loadStats();
            } else {
                throw new Error(res.message || 'Tải lên thất bại');
            }
        } catch (err) {
            this.setState({
                uploadStatus: 'error',
                uploadMessage: err.message || 'Không thể tải và xử lý tài liệu'
            });
            info.onError?.(err);
            message.error(err.message || 'Lỗi khi upload file');
        } finally {
            this.setState({ uploading: false });
        }
    };

    handleDelete = async (fileId) => {
        try {
            const companyId = this.props.companySelected?.company_id || 0;
            const res = await fetchData({
                url: `api/v1/app/ai/documents/${fileId}`,
                method: 'delete',
                data: { company_id: companyId }
            });

            if (res.code === 200) {
                message.success('Đã xóa file thành công');
                this.loadFiles();
                this.loadStats();
            } else {
                message.error(res.message || 'Xóa thất bại');
            }
        } catch (err) {
            message.error('Lỗi khi xóa file');
        }
    };

    handlePreview = async (fileId) => {
        try {
            this.setState({ previewVisible: true, previewLoading: true, previewContent: null });
            const companyId = this.props.companySelected?.company_id || 0;
            const res = await fetchData({
                url: `api/v1/app/ai/documents/${fileId}/content`,
                data: { company_id: companyId }
            });

            if (res.code === 200) {
                this.setState({ previewContent: res.data });
            } else {
                message.error('Không thể lấy nội dung tài liệu');
            }
        } catch (err) {
            message.error('Lỗi khi tải nội dung tài liệu');
        } finally {
            this.setState({ previewLoading: false });
        }
    };

    handlePageChange = (page, pageSize) => {
        this.setState({ page, pageSize }, this.loadFiles);
    };

    getUploadStepIndex = (stage) => {
        const stepByStage = {
            idle: 0,
            uploading: 0,
            received: 1,
            forwarding: 1,
            storing: 1,
            stored: 2,
            parsing: 2,
            parsed: 3,
            analyzing_images: 3,
            embedding: 4,
            indexing: 4,
            finalizing: 5,
            completed: 5
        };
        return stepByStage[stage] ?? 0;
    };

    columns = [
        {
            title: 'Tên file',
            dataIndex: 'originalName',
            key: 'originalName',
            ellipsis: true,
            width: 250,
            render: (val) => (
                <span>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    {val}
                </span>
            )
        },
        {
            title: 'Kích thước',
            dataIndex: 'fileSize',
            key: 'fileSize',
            width: 100,
            render: (val) => {
                if (val < 1024) return `${val} B`;
                if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
                return `${(val / 1024 / 1024).toFixed(1)} MB`;
            }
        },
        {
            title: 'Chunks',
            dataIndex: 'chunksCount',
            key: 'chunksCount',
            width: 80
        },
        {
            title: 'Danh mục',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            render: (val) => val ? <Tag color="blue">{val}</Tag> : '-'
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (val) => {
                const colors = {
                    'completed': 'green',
                    'processing': 'orange',
                    'failed': 'red'
                };
                const labels = {
                    'completed': 'Hoàn thành',
                    'processing': 'Đang xử lý',
                    'failed': 'Thất bại'
                };
                return <Tag color={colors[val]}>{labels[val] || val}</Tag>;
            }
        },
        {
            title: 'Ngày tải',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (val) => moment(val).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button 
                        type="text" 
                        icon={<EyeOutlined />} 
                        onClick={() => this.handlePreview(record.id)}
                        disabled={record.status !== 'completed'}
                    />
                    <Popconfirm
                        title="Bạn có chắc muốn xóa file này?"
                        description="Tất cả dữ liệu liên quan sẽ bị xóa."
                        onConfirm={() => this.handleDelete(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            )
        }
    ];

    render() {
        const {
            loading,
            uploading,
            uploadStatus,
            uploadPercent,
            processingPercent,
            uploadStage,
            uploadMessage,
            uploadFileName,
            uploadEvents,
            files,
            total,
            stats,
            page,
            pageSize,
            category,
            tags
        } = this.state;
        const uploadStep = this.getUploadStepIndex(uploadStage);
        const showUploadProgress = uploadStatus !== 'idle';

        return (
            <div style={{ padding: 20 }}>
                {/* Stats Cards */}
                <Row gutter={16} style={{ marginBottom: 20 }}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Tổng tệp"
                                value={stats?.totalFiles || 0}
                                prefix={<FileTextOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Tổng tài liệu"
                                value={stats?.totalDocuments || 0}
                                prefix={<DatabaseOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Hoàn thành"
                                value={stats?.completedFiles || 0}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Thất bại"
                                value={stats?.failedFiles || 0}
                                valueStyle={{ color: stats?.failedFiles > 0 ? '#cf1322' : undefined }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Upload Section */}
                <Card title="Tải lên tài liệu mới" style={{ marginBottom: 20 }}>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Input
                                placeholder="Danh mục (VD: báo giá, hướng dẫn)"
                                value={category}
                                onChange={(e) => this.setState({ category: e.target.value })}
                            />
                        </Col>
                        <Col span={6}>
                            <Input
                                placeholder="Tags (cách nhau bởi dấu phẩy)"
                                value={tags}
                                onChange={(e) => this.setState({ tags: e.target.value })}
                            />
                        </Col>
                        <Col span={12}>
                            <Upload
                                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.html,.md"
                                showUploadList={false}
                                customRequest={this.handleUpload}
                                disabled={uploading}
                            >
                                <Button 
                                    type="primary" 
                                    icon={<UploadOutlined />}
                                    loading={uploading}
                                >
                                    {uploading ? 'Đang xử lý...' : 'Chọn tài liệu để tải lên'}
                                </Button>
                            </Upload>
                            <span style={{ marginLeft: 12, color: '#999' }}>
                                Hỗ trợ đa định dạng, tối đa 50MB
                            </span>
                        </Col>
                    </Row>

                    {showUploadProgress && (
                        <div style={{
                            marginTop: 20,
                            padding: '18px 20px',
                            background: uploadStatus === 'error' ? '#fff7f6' : '#f7fafc',
                            border: `1px solid ${uploadStatus === 'error' ? '#ffccc7' : '#dfe7ee'}`,
                            borderRadius: 8
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, color: '#6b7785', marginBottom: 3 }}>Tài liệu đang xử lý</div>
                                    <div style={{ fontWeight: 600, color: '#26323d', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {uploadFileName}
                                    </div>
                                </div>
                                <Tag color={uploadStatus === 'error' ? 'red' : uploadStatus === 'success' ? 'green' : 'processing'}>
                                    {uploadStatus === 'error' ? 'Có lỗi' : uploadStatus === 'success' ? 'Hoàn thành' : 'Đang xử lý'}
                                </Tag>
                            </div>

                            <Row gutter={24} style={{ marginBottom: 18 }}>
                                <Col xs={24} md={8}>
                                    <div style={{ fontSize: 12, color: '#6b7785', marginBottom: 5 }}>1. Tải file lên máy chủ</div>
                                    <Progress
                                        percent={uploadPercent}
                                        size="small"
                                        status={uploadStatus === 'error' && uploadPercent < 100 ? 'exception' : uploadPercent === 100 ? 'success' : 'active'}
                                    />
                                </Col>
                                <Col xs={24} md={16}>
                                    <div style={{ fontSize: 12, color: '#6b7785', marginBottom: 5 }}>2. AI đọc và lập chỉ mục</div>
                                    <Progress
                                        percent={processingPercent}
                                        size="small"
                                        strokeColor="#2f7d6d"
                                        status={uploadStatus === 'error' ? 'exception' : uploadStatus === 'success' ? 'success' : 'active'}
                                    />
                                </Col>
                            </Row>

                            <Steps current={uploadStep} size="small" status={uploadStatus === 'error' ? 'error' : 'process'}>
                                <Step title="Tải lên" />
                                <Step title="Lưu file" />
                                <Step title="Đọc nội dung" />
                                <Step title="Phân tích ảnh" />
                                <Step title="Tạo dữ liệu AI" />
                                <Step title="Hoàn tất" />
                            </Steps>

                            <Alert
                                style={{ marginTop: 18 }}
                                type={uploadStatus === 'error' ? 'error' : uploadStatus === 'success' ? 'success' : 'info'}
                                showIcon
                                message={uploadMessage}
                                description={uploadStatus !== 'error' && uploadEvents.length > 1 ? (
                                    <div style={{ marginTop: 4 }}>
                                        {uploadEvents.slice(-3).map((event) => (
                                            <div key={event.id} style={{ color: '#66727f', fontSize: 12, lineHeight: 1.7 }}>
                                                {event.message}
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            />
                        </div>
                    )}
                </Card>

                {/* Files Table */}
                <Card title="Danh sách tài liệu">
                    <Spin spinning={loading}>
                        <Table
                            columns={this.columns}
                            dataSource={files}
                            rowKey="id"
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: total,
                                onChange: this.handlePageChange,
                                showSizeChanger: true,
                                showTotal: (total) => `Tổng ${total} tệp`
                            }}
                            size="small"
                        />
                    </Spin>
                </Card>

                {/* Preview Modal */}
                <Modal
                    title={this.state.previewContent ? `Nội dung: ${this.state.previewContent.fileName}` : 'Đang tải nội dung...'}
                    open={this.state.previewVisible}
                    onCancel={() => this.setState({ previewVisible: false })}
                    footer={[
                        <Button key="close" onClick={() => this.setState({ previewVisible: false })}>
                            Đóng
                        </Button>
                    ]}
                    width={800}
                    bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
                >
                    <Spin spinning={this.state.previewLoading}>
                        {this.state.previewContent ? (
                            <div>
                                {this.state.previewContent.chunks && this.state.previewContent.chunks.length > 0 ? (
                                    this.state.previewContent.chunks.map((chunk, index) => (
                                        <div key={index} style={{ marginBottom: 15 }}>
                                            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#1890ff', marginBottom: 4 }}>
                                                Chunk #{chunk.index}
                                            </div>
                                            <div style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', padding: 15, borderRadius: 5, borderLeft: '4px solid #1890ff' }}>
                                                {chunk.content}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', padding: 15, borderRadius: 5 }}>
                                        {this.state.previewContent.content || 'Nội dung trống.'}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {this.state.previewLoading ? 'Đang đọc dữ liệu từ cơ sở dữ liệu vector...' : 'Không có nội dung.'}
                            </div>
                        )}
                    </Spin>
                </Modal>
            </div>
        );
    }
}
